import { FC, useState } from "react";
import styles from "./Sidebar.module.scss";
import { clientContent } from "../../client/content";
import { PulseLogo } from "../PulseLogo";

export interface SidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
}

const informationCategories = [
  {
    name: "Asset Data",
    children: ["Building", "Electrical", "Mechanical"],
  },
  {
    name: "Certification",
    children: ["Building", "Electrical", "Mechanical"],
  },
  {
    name: "Controls",
    children: ["Electrical", "Mechanical"],
  },
  {
    name: "Descriptions",
    children: ["Building", "Electrical", "Mechanical"],
  },
  {
    name: "Design Criteria",
    children: ["Building", "Electrical", "Mechanical"],
  },
  {
    name: "Environmental",
    children: [],
  },
  {
    name: "General Information",
    children: [],
  },
  {
    name: "Health & Safety",
    children: ["Building", "Electrical", "Mechanical"],
  },
  {
    name: "Maintenance",
    children: ["Building", "Electrical", "Mechanical"],
  },
  {
    name: "Manufacturer Information",
    children: ["Building", "Electrical", "Mechanical"],
  },
  {
    name: "Operating Procedures",
    children: ["Building", "Electrical", "Mechanical"],
  },
  {
    name: "Schedules",
    children: ["Building", "Electrical", "Mechanical"],
  },
];

export const Sidebar: FC<SidebarProps> = ({ isOpen = false, onClose }) => {
  const currentPath =
    typeof window !== "undefined" ? window.location.pathname : "";
  const isDesignCriteriaActive = currentPath.startsWith("/design-criteria");
  const isDrawingsActive = currentPath.startsWith("/drawings");
  const isAssetDataActive =
    currentPath.startsWith("/asset-data") ||
    currentPath.startsWith("/mobile-asset");
  const isSearchActive = currentPath.startsWith("/search");
  const isOverviewActive = currentPath === "/" || currentPath === "";
  const isHealthSafetyActive =
    currentPath.startsWith("/health-and-safety") ||
    currentPath.startsWith("/health-safety") ||
    currentPath.startsWith("/hs");

  const [openCategories, setOpenCategories] = useState<Record<string, boolean>>(
    () => ({
      "Health & Safety": isHealthSafetyActive,
      "Design Criteria": isDesignCriteriaActive,
    }),
  );

  const toggleCategory = (
    name: string,
    hasChildren: boolean,
    e: React.MouseEvent,
  ) => {
    if (hasChildren) {
      e.preventDefault();
      setOpenCategories((prev) => ({
        ...prev,
        [name]: !prev[name],
      }));
    }
  };

  return (
    <>
      <div
        className={`${styles.sidebarBackdrop} ${isOpen ? styles.open : ""}`}
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        className={`${styles.sidebar} ${isOpen ? styles.open : ""}`}
        aria-label="Main navigation"
      >
        <div className={styles.topSection}>
          <div className={styles.brandHeader}>
            <a
              href="/"
              className={styles.brand}
              aria-label="Pulse Building Information Home"
            >
              <PulseLogo variant="full" theme="dark" />
            </a>
            {onClose && (
              <button
                type="button"
                className={styles.closeButton}
                onClick={onClose}
                aria-label="Close navigation sidebar"
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}
          </div>

          <div className={styles.siteSwitcher}>
            <span className={styles.sectionLabel} id="current-site-label">
              CURRENT SITE
            </span>
            <button
              type="button"
              className={styles.siteCard}
              aria-labelledby="current-site-label"
            >
              <span className={styles.siteName}>
                {clientContent.clientName}
              </span>
              <span className={styles.siteSubtext}>
                {clientContent.siteLocation}
              </span>
            </button>
          </div>

          <a
            href="https://pulseom.co.uk"
            className={styles.navLink}
            target="_blank"
            rel="noopener noreferrer"
          >
            <span>Back to pulseom.co.uk</span>
          </a>

          <nav className={styles.navSection} aria-label="Explore sections">
            <span className={styles.sectionLabel}>KEY TEST AREAS</span>
            <ul className={styles.exploreList}>
              <li>
                <a
                  href="/"
                  className={`${styles.exploreButton} ${isOverviewActive ? styles.active : ""}`}
                  aria-current={isOverviewActive ? "page" : undefined}
                >
                  <span className={styles.navIcon} aria-hidden="true">
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                      <polyline points="9 22 9 12 15 12 15 22" />
                    </svg>
                  </span>
                  <span className={styles.navText}>Overview</span>
                </a>
              </li>
              <li>
                <a
                  href="/search"
                  className={`${styles.exploreButton} ${isSearchActive ? styles.active : ""}`}
                  aria-current={isSearchActive ? "page" : undefined}
                >
                  <span className={styles.navIcon} aria-hidden="true">
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="11" cy="11" r="8" />
                      <line x1="21" y1="21" x2="16.65" y2="16.65" />
                    </svg>
                  </span>
                  <span className={styles.navText}>Search</span>
                </a>
              </li>
              <li>
                <a
                  href="/drawings"
                  className={`${styles.exploreButton} ${isDrawingsActive ? styles.active : ""}`}
                  aria-current={isDrawingsActive ? "page" : undefined}
                >
                  <span className={styles.navIcon} aria-hidden="true">
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                      <line x1="16" y1="13" x2="8" y2="13" />
                      <line x1="16" y1="17" x2="8" y2="17" />
                    </svg>
                  </span>
                  <span className={styles.navText}>Drawings</span>
                  <span className={styles.navChevron} aria-hidden="true">
                    ›
                  </span>
                </a>
              </li>
              <li>
                <a
                  href="/design-criteria/mechanical"
                  className={`${styles.exploreButton} ${isDesignCriteriaActive ? styles.active : ""}`}
                  aria-current={isDesignCriteriaActive ? "page" : undefined}
                >
                  <span className={styles.navIcon} aria-hidden="true">
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="12" cy="12" r="3" />
                      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06-.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
                    </svg>
                  </span>
                  <span className={styles.navText}>
                    Design Criteria Mechanical
                  </span>
                </a>
              </li>
              <li>
                <a
                  href="/asset-data/ahu-003"
                  className={`${styles.exploreButton} ${isAssetDataActive ? styles.active : ""}`}
                  aria-current={isAssetDataActive ? "page" : undefined}
                >
                  <span className={styles.navIcon} aria-hidden="true">
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                      <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
                      <line x1="12" y1="22.08" x2="12" y2="12" />
                    </svg>
                  </span>
                  <span className={styles.navText}>Asset Concept</span>
                </a>
              </li>
            </ul>
          </nav>

          <nav className={styles.navSection} aria-label="Information sections">
            <span className={styles.sectionLabel}>INFORMATION</span>
            <ul className={styles.navList}>
              {informationCategories.map((cat) => {
                const hasChildren = cat.children.length > 0;
                const isOpen = !!openCategories[cat.name];

                return (
                  <li key={cat.name} className={styles.infoCategoryItem}>
                    <a
                      href={
                        cat.name === "Design Criteria"
                          ? "/design-criteria/mechanical"
                          : cat.name === "Health & Safety"
                            ? "/health-and-safety/electrical"
                            : "/more-info"
                      }
                      className={`${styles.navLink} ${
                        (cat.name === "Design Criteria" &&
                          isDesignCriteriaActive) ||
                        (cat.name === "Health & Safety" && isHealthSafetyActive)
                          ? styles.active
                          : ""
                      }`}
                      onClick={(e) => toggleCategory(cat.name, hasChildren, e)}
                    >
                      <span>{cat.name}</span>
                      {hasChildren && (
                        <span
                          className={`${styles.navChevron} ${isOpen ? styles.open : ""}`}
                          aria-hidden="true"
                        >
                          ›
                        </span>
                      )}
                    </a>
                    {hasChildren && isOpen && (
                      <ul className={styles.childNavList}>
                        {cat.children.map((child) => {
                          const isChildActive =
                            (cat.name === "Design Criteria" &&
                              child.toLowerCase() === "mechanical" &&
                              isDesignCriteriaActive) ||
                            (cat.name === "Health & Safety" &&
                              child.toLowerCase() === "electrical" &&
                              isHealthSafetyActive);

                          const childHref =
                            cat.name === "Design Criteria" &&
                            child.toLowerCase() === "mechanical"
                              ? "/design-criteria/mechanical"
                              : cat.name === "Health & Safety" &&
                                  child.toLowerCase() === "electrical"
                                ? "/health-and-safety/electrical"
                                : "/more-info";

                          return (
                            <li key={child}>
                              <a
                                href={childHref}
                                className={`${styles.childNavLink} ${isChildActive ? styles.active : ""}`}
                                aria-current={
                                  isChildActive ? "page" : undefined
                                }
                              >
                                {child}
                              </a>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          </nav>
        </div>

        <div className={styles.helpSection}>
          <span className={styles.helpLabel}>Need help?</span>
          <div className={styles.helpLinks}>
            <a href="/more-info">Pulse guide</a>
            <span className={styles.separator} aria-hidden="true">
              ·
            </span>
            <a href="/more-info">Contact support</a>
          </div>
          <div className={styles.copyright}>Copyright pulseom.co.uk 2026</div>
        </div>
      </aside>
    </>
  );
};
